import { CloseButton } from '@ui/shared/Button';
import filter from 'lodash/filter';
import * as React from 'react';
import Toggle from 'react-toggle';
import 'react-toggle/style.css';
import styled from 'styled-components';
import { LIST_ROW_HEIGHTS } from '../../../constants/list_row_heights';
import { tagsProvider } from '../../../data/providers/tagsProvider';
import { ITag } from '../../../data/types';
import { Column } from '../../layout/Column';
import { Row } from '../../layout/Row';
import { CardBody } from '../../shared/card/CardBody';
import { CardFooter, CardFooterLeft } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { SearchInput } from '../../shared/form/SearchInput';
import { List } from '../../shared/List/List';
import { ListHeader } from '../../shared/List/ListHeader';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { NoResults } from '../../shared/NoResults';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';
import { AddTag } from './AddTag';
import { TagRow } from './TagRow';

const SelectSwap = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding-right: 10px;
  label {
    padding-right: 10px;
    font-size: 1.2em;
  }
`;

interface IProps {
  onClose: () => void;
}

const Tags = (props: IProps): React.ReactElement => {
  const [tags, setTags] = React.useState<ITag[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState('');
  const [showDisabled, setShowDisabled] = React.useState(false);

  React.useEffect((): (() => void) => {
    const subscriber = tagsProvider().subscribe((mTags: ITag[]): void => {
      if (mTags.length > 0) {
        setLoading(false);
        setTags(mTags);
      }
    });
    return (): void => {
      subscriber.unsubscribe();
    };
  }, []);

  const updateSearch = (value: string): void => setSearch(value);

  const runSearch = (mTags: ITag[], mSearch: string): ITag[] => {
    if (mSearch) {
      return mTags.filter((item: ITag): boolean => {
        return item.name.toLowerCase().includes(mSearch.toLowerCase());
      }) as ITag[];
    } else {
      return mTags;
    }
  };

  const data = React.useMemo((): ITag[] => {
    return filter(runSearch(tags, search), (item: ITag): boolean => {
      return showDisabled ? true : item.active;
    });
  }, [tags, search, showDisabled]);

  const rowRenderer = ({
    index,
    key,
    style,
  }: {
    index: number;
    key: string;
    style: React.CSSProperties;
  }): React.ReactElement => {
    const mTag = data[index];
    return (
      <TagRow
        key={key}
        tag={mTag}
        style={style}
        label={mTag.name}
        description={mTag.description || null}
      />
    );
  };

  const clearFilter = (): void => {
    setSearch('');
  };

  const updateShowDisabled = (): void => {
    setShowDisabled(!showDisabled);
  };

  return (
    <PopupCard onClose={props.onClose} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8} borderless>
          <CardHeaderLeft>
            <h5>Tags</h5>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody style={{ backgroundColor: 'var(--color-list-background)' }}>
          <CardHeader withListHeader notRounded surface={8}>
            <Column style={{ paddingLeft: '10px', paddingRight: '10px' }}>
              <Row>
                <CardHeaderLeft>
                  <SearchInput
                    value={search}
                    onChange={updateSearch}
                    onClear={clearFilter}
                    style={{ marginLeft: '0px' }}
                  />
                </CardHeaderLeft>
                <CardHeaderRight>
                  <AddTag />
                </CardHeaderRight>
              </Row>
              <ListHeader labels={['Name', 'Weight Excluded', 'Edit']} />
            </Column>
          </CardHeader>
          {loading ? (
            <LinearLoading />
          ) : data && data.length > 0 ? (
            <List
              listLength={data.length}
              rowRenderer={rowRenderer}
              rowHeight={LIST_ROW_HEIGHTS.normal}
            />
          ) : (
            <NoResults message='No Tags Found' />
          )}
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft style={{ paddingLeft: '15px' }}>
            <SelectSwap>
              <label htmlFor='enable-pdf'>Show Disabled</label>
              <Toggle
                className='toggle'
                icons={false}
                checked={showDisabled}
                onChange={updateShowDisabled}
              />
            </SelectSwap>
          </CardFooterLeft>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

export { Tags };
